import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';
import { getFirebase } from 'firestorter';
import {
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import DialogRoundedModal from '../../../../../../../components/DialogRoundedModal';
import { LightTag } from '../../../../../../../components/Tags';
import { SaveButton, CancelButton } from '../../../../../../../components/Button/ActionButtons';
import SearchBar from '../../../../../../../chat/components/SearchBar';
import LoadingOverlay from '../../../../../LoadingOverlay';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import useToast from '../../../../../../hooks/useToast';
import { CommunityType } from '../../../../../../Model/Community';
import { DateFormat } from '../../../../../../../utils/date';

import {
  StyledPeopleIcon,
  ActionButtonsRow,
  SearchContainer,
  CommunityList,
  CommunityRow,
  RowLabel,
  NoResultsText,
} from './styles';
import texts from './texts.json';

const BADGE_BY_TYPE = {
  [CommunityType.POD]: texts.assignedViaPod,
  [CommunityType.TAG]: texts.assignedViaTag,
};

const frozenBadge = (frozenAt) => format(texts.readOnlySince, {
  date: moment(frozenAt?.toDate?.()).format(DateFormat.MONTH_NAME_DATE_FORMAT),
});

const EditCommunitiesModal = ({
  isOpen,
  onDidDismiss,
  userDoc,
  coachCommunities,
  otherCoachCommunities,
  unlistedCommunities,
  productCommunityIds,
  grantedIds,
  genericCommunities,
  communityAccess,
}) => {
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const frozen = useMemo(() => communityAccess?.frozen || {}, [communityAccess]);
  const defaultCommunityId = communityAccess?.defaultCommunityId || null;

  // Product and frozen communities are read-only.
  const lockedIds = useMemo(
    () => new Set([...productCommunityIds, ...Object.keys(frozen)]),
    [productCommunityIds, frozen],
  );
  const customCommunities = useMemo(
    () => coachCommunities.filter(
      (community) => community.type === CommunityType.CUSTOM && !lockedIds.has(community.id),
    ),
    [coachCommunities, lockedIds],
  );
  // An archived product community is dropped by getByCoach, so it reaches us as unlisted. It stays
  // read-only regardless: the server re-derives it from product.communityId, so revoking it wouldn't hold.
  const revocableUnlisted = useMemo(
    () => unlistedCommunities.filter((community) => !lockedIds.has(community.id)),
    [unlistedCommunities, lockedIds],
  );
  const otherCoachRows = useMemo(
    () => otherCoachCommunities
      .filter((community) => grantedIds.includes(community.id))
      .map(({ id, name }) => ({
        id,
        name,
        badge: texts.assignedByAnotherCoach,
        editable: false,
      })),
    [otherCoachCommunities, grantedIds],
  );
  const initiallyGranted = useMemo(
    () => [...customCommunities, ...revocableUnlisted]
      .filter((community) => grantedIds.includes(community.id))
      .map((community) => community.id),
    [customCommunities, revocableUnlisted, grantedIds],
  );

  const [selectedIds, setSelectedIds] = useState(initiallyGranted);
  const [search, setSearch] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Generic (category) rows first, then the client's pod/tag memberships, then the editable
  // custom ones. A client holding two products belongs to two generic audiences.
  const rows = useMemo(() => {
    // No server default: every generic row is default.
    const genericRows = [...genericCommunities]
      .sort((a, b) => (b.id === defaultCommunityId) - (a.id === defaultCommunityId))
      .map(({ id, name }) => ({
        id,
        name,
        badge: !defaultCommunityId || id === defaultCommunityId ? texts.defaultAccess : texts.assignedViaProduct,
        editable: false,
      }));

    const derivedRows = coachCommunities
      .filter((community) => grantedIds.includes(community.id) && BADGE_BY_TYPE[community.type])
      .map(({ id, name, type }) => ({
        id,
        name,
        badge: BADGE_BY_TYPE[type],
        editable: false,
      }));

    const productRows = [...coachCommunities, ...unlistedCommunities]
      .filter((community) => lockedIds.has(community.id) && (grantedIds.includes(community.id) || frozen[community.id]))
      .map(({ id, name }) => ({
        id,
        name,
        badge: texts.assignedViaProduct,
        editable: false,
      }));

    // Grants the coach's list can't explain. Editable so they can be taken back - nothing else will.
    const unlistedRows = revocableUnlisted
      .filter((community) => grantedIds.includes(community.id))
      .map(({ id, name, isArchived }) => ({
        id,
        name,
        badge: isArchived ? texts.archived : texts.noLongerAvailable,
        editable: true,
      }));

    const customRows = customCommunities.map(({ id, name }) => ({
      id,
      name,
      editable: true,
    }));

    const allRows = [...genericRows, ...derivedRows, ...productRows, ...otherCoachRows, ...unlistedRows, ...customRows]
      .map((row) => {
        if (frozen[row.id]) return { ...row, badge: frozenBadge(frozen[row.id]), editable: false };
        if (row.id === defaultCommunityId) return { ...row, badge: texts.defaultAccess, editable: false };
        return row;
      });
    // Dedupe by id; the first row wins.
    const seenIds = new Set();
    return allRows.filter((row) => {
      if (seenIds.has(row.id)) return false;
      seenIds.add(row.id);
      return true;
    });
  }, [genericCommunities, coachCommunities, customCommunities, unlistedCommunities, revocableUnlisted,
    otherCoachRows, lockedIds, grantedIds, frozen, defaultCommunityId]);

  const filteredRows = useMemo(
    () => rows.filter((row) => row.name.toLowerCase().includes(search.toLowerCase())),
    [rows, search],
  );

  const hasEditableRows = useMemo(() => rows.some((row) => row.editable), [rows]);

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  }, []);

  const handleSearchChange = useCallback((event) => setSearch(event.target.value), []);
  const handleClearSearch = useCallback(() => setSearch(''), []);

  const handleSave = useCallback(async () => {
    // Diff the current selection against the initial grant: newly checked ids to add, unchecked to remove.
    const toAdd = selectedIds.filter((id) => !initiallyGranted.includes(id));
    const toRemove = initiallyGranted.filter((id) => !selectedIds.includes(id));

    if (!toAdd.length && !toRemove.length) {
      onDidDismiss();
      return;
    }

    try {
      setIsProcessing(true);
      const { FieldValue } = getFirebase().firestore;
      if (toAdd.length) {
        await userDoc.ref.update({ communityIds: FieldValue.arrayUnion(...toAdd) });
      }
      if (toRemove.length) {
        await userDoc.ref.update({ communityIds: FieldValue.arrayRemove(...toRemove) });
      }
      showToast(texts.savedToast);
      onDidDismiss();
    } catch (error) {
      showToast(format(texts.failedToast, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsProcessing(false);
      }
    }
  }, [userDoc, initiallyGranted, selectedIds, showToast, onDidDismiss, isComponentMountedRef]);

  return (
    <>
      <DialogRoundedModal
        open={isOpen}
        onClose={onDidDismiss}
        title={texts.title}
        description={texts.description}
        IconComponent={<StyledPeopleIcon />}
        maxWidth="sm"
        fullWidth
        actionButtons={(
          <ActionButtonsRow>
            <SaveButton onClick={handleSave} disabled={isProcessing}>
              {texts.save}
            </SaveButton>
            <CancelButton onClick={onDidDismiss}>
              {texts.cancel}
            </CancelButton>
          </ActionButtonsRow>
        )}
      >
        <SearchContainer>
          <SearchBar
            value={search}
            onChange={handleSearchChange}
            clearSearch={handleClearSearch}
            placeholder={texts.searchPlaceholder}
          />
        </SearchContainer>
        <CommunityList>
          {filteredRows.map(({
            id, editable, name, badge,
          }) => (
            <CommunityRow key={id}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={editable ? selectedIds.includes(id) : true}
                    disabled={!editable}
                    onChange={editable ? () => toggle(id) : undefined}
                  />
                )}
                label={(
                  <RowLabel>
                    {name}
                    {badge && <LightTag>{badge}</LightTag>}
                  </RowLabel>
                )}
              />
            </CommunityRow>
          ))}
          {!!search && filteredRows.length === 0 && (
            <NoResultsText>{texts.noResults}</NoResultsText>
          )}
          {/* The generic row always renders, so the empty state keys off what's actually selectable. */}
          {!search && !hasEditableRows && (
            <NoResultsText>{texts.noCustomCommunities}</NoResultsText>
          )}
        </CommunityList>
      </DialogRoundedModal>
      <LoadingOverlay isLoading={isProcessing} />
    </>
  );
};

EditCommunitiesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  userDoc: PropTypes.object.isRequired,
  coachCommunities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  })).isRequired,
  otherCoachCommunities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  unlistedCommunities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isArchived: PropTypes.bool,
  })).isRequired,
  productCommunityIds: PropTypes.arrayOf(PropTypes.string),
  grantedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  genericCommunities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  communityAccess: PropTypes.shape({
    defaultCommunityId: PropTypes.string,
    frozen: PropTypes.objectOf(PropTypes.object),
  }),
};

EditCommunitiesModal.defaultProps = {
  productCommunityIds: [],
  communityAccess: null,
};

export default EditCommunitiesModal;
